import React, { useState, useEffect, useRef } from 'react';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, sendPasswordResetEmail, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from 'react-router-dom';
import { auth, db, fetchRemoteConfig } from '../firebase-config';
import { AuthEvents, SecureLog } from '../utils/SecurityLogger';
import THEME from '../theme';
import { motion, AnimatePresence } from 'framer-motion';

// ======================== RATE LIMITING ========================
// [SECURITY] Client-side rate limiting (matches Android: MAX_FAILED_ATTEMPTS=5, LOCKOUT=15min)
const RATE_LIMIT_KEY = 'resido_login_attempts';
const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 15 * 60 * 1000; // 15 minutes

function getRateLimitState() {
  try {
    const raw = localStorage.getItem(RATE_LIMIT_KEY);
    if (!raw) return { attempts: 0, lockoutUntil: 0 };
    return JSON.parse(raw);
  } catch { return { attempts: 0, lockoutUntil: 0 }; }
}

function recordFailedAttempt() {
  const state = getRateLimitState();
  state.attempts += 1;
  if (state.attempts >= MAX_ATTEMPTS) {
    state.lockoutUntil = Date.now() + LOCKOUT_MS;
    state.attempts = 0; // Reset count, lockout handles protection
  }
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(state));
  return state;
}

function clearRateLimit() {
  localStorage.removeItem(RATE_LIMIT_KEY);
}

function isLockedOut() {
  const state = getRateLimitState();
  if (state.lockoutUntil > Date.now()) return true;
  if (state.lockoutUntil > 0 && state.lockoutUntil <= Date.now()) {
    clearRateLimit(); // Lockout expired
  }
  return false;
}

function getLockoutRemaining() {
  const state = getRateLimitState();
  const remaining = state.lockoutUntil - Date.now();
  return remaining > 0 ? remaining : 0;
}

// ======================== ERROR SANITIZATION ========================
// [SECURITY] Map Firebase error codes to user-friendly messages (prevents info leakage)
const ERROR_MESSAGES = {
  'auth/user-not-found': 'Invalid email or password. Please try again.',
  'auth/invalid-credential': 'Invalid email or password. Please try again.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled. Contact support for help.',
  'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
  'auth/popup-closed-by-user': 'Sign-in popup was closed. Please try again.',
  'auth/cancelled-popup-request': null, // Silently ignore
  'auth/network-request-failed': 'Network error. Please check your internet connection.',
  'auth/popup-blocked': 'Pop-up was blocked by your browser. Please allow pop-ups for this site.',
  'auth/account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method.',
};

function getSafeErrorMessage(error) {
  if (error?.code && ERROR_MESSAGES[error.code] !== undefined) {
    return ERROR_MESSAGES[error.code]; // null means silently ignore
  }
  SecureLog.warn('[Login] Unmapped error code:', error?.code, error?.message);
  return 'An unexpected error occurred. Please try again.';
}

// ======================== PASSWORD STRENGTH ========================
function getPasswordStrength(password) {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 6) score++;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score: 1, label: 'Weak', color: '#EF4444' };
  if (score <= 2) return { score: 2, label: 'Fair', color: '#F59E0B' };
  if (score <= 3) return { score: 3, label: 'Good', color: '#F59E0B' };
  if (score <= 4) return { score: 4, label: 'Strong', color: '#14B8A6' };
  return { score: 5, label: 'Very Strong', color: '#10B981' };
}

// ======================== MAIN COMPONENT ========================
function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState({ text: '', type: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const navigate = useNavigate();

  // Remote Config state
  const [allowNewGoogleSignup, setAllowNewGoogleSignup] = useState(false);
  const [demoInstanceEnabled, setDemoInstanceEnabled] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Terms acceptance state
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  // [SECURITY] Honeypot field — if filled, silently reject
  const [honeypot, setHoneypot] = useState('');

  // [SECURITY] Rate limiting state
  const [lockoutTime, setLockoutTime] = useState(getLockoutRemaining());
  const lockoutInterval = useRef(null);

  // [FEATURE] Remember Me
  const [rememberMe, setRememberMe] = useState(false);

  // [ANIMATION] Login success state
  const [loginSuccess, setLoginSuccess] = useState(false);

  // [NEW] Consent dialog state (matches Android LoginScreen.kt)
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [authMethodForConsent, setAuthMethodForConsent] = useState(''); // 'google' or 'email'

  // Lockout countdown
  useEffect(() => {
    if (lockoutTime > 0) {
      lockoutInterval.current = setInterval(() => {
        const remaining = getLockoutRemaining();
        setLockoutTime(remaining);
        if (remaining <= 0) clearInterval(lockoutInterval.current);
      }, 1000);
      return () => clearInterval(lockoutInterval.current);
    }
  }, [lockoutTime]);

  // Fetch remote config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await fetchRemoteConfig();
        setAllowNewGoogleSignup(config.allowNewGoogleSignup);
        setDemoInstanceEnabled(config.demoInstanceEnabled);
      } catch (err) {
        SecureLog.error("Failed to load remote config:", err);
      } finally {
        setConfigLoaded(true);
      }
    };
    loadConfig();
  }, []);

  // Check session timeout reason from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'session_timeout') {
      setError('Your session timed out due to inactivity. Please sign in again.');
    }
  }, []);

  const navigateAfterLogin = async (user) => {
    setLoginSuccess(true);
    // Brief animation before redirect
    await new Promise(resolve => setTimeout(resolve, 800));
    const docRef = doc(db, "users", user.uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().userType === 'android') {
      await signOut(auth);
      AuthEvents.androidAccountBlocked();
      setLoginSuccess(false);
      setError('This account was created on the Android app. Please use the Resido Android app to sign in.');
      return;
    }

    if (docSnap.exists() && docSnap.data().isPhoneVerified) {
      navigate('/dashboard');
    } else {
      navigate('/institute-selection');
    }
  };

  // [NEW] Trigger consent dialog for email login (matches Android)
  const requestEmailLogin = (e) => {
    e.preventDefault();

    // [SECURITY] Honeypot check
    if (honeypot) {
      SecureLog.warn('[Security] Honeypot triggered — likely bot submission.');
      setError('An unexpected error occurred. Please try again.');
      return;
    }

    if (!isTermsAccepted) {
      setError('Please accept the Terms & Conditions to continue.');
      return;
    }

    setAuthMethodForConsent('email');
    setShowConsentDialog(true);
  };

  const handleLogin = async () => {

    // [SECURITY] Rate limit check
    if (isLockedOut()) {
      const mins = Math.ceil(getLockoutRemaining() / 60000);
      setError(`Too many failed attempts. Please try again in ${mins} minute${mins !== 1 ? 's' : ''}.`);
      setLockoutTime(getLockoutRemaining());
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      AuthEvents.loginSuccess('email');
      clearRateLimit();
      SecureLog.info(`User ${result.user.uid} email login successful.`);
      await navigateAfterLogin(result.user);
    } catch (error) {
      AuthEvents.loginFailure('email', error.code);
      const safeMessage = getSafeErrorMessage(error);
      if (safeMessage) setError(safeMessage);

      // Record failed attempt for rate limiting
      const state = recordFailedAttempt();
      if (state.lockoutUntil > Date.now()) {
        setLockoutTime(getLockoutRemaining());
        setError(`Too many failed attempts. Account locked for 15 minutes.`);
      } else {
        const remaining = MAX_ATTEMPTS - state.attempts;
        if (remaining <= 2 && remaining > 0) {
          setError(prev => prev + ` (${remaining} attempt${remaining !== 1 ? 's' : ''} remaining)`);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // [NEW] Trigger consent dialog instead of directly signing in (matches Android)
  const requestGoogleSignIn = () => {
    if (!isTermsAccepted) {
      setError('Please accept the Terms & Conditions to continue.');
      return;
    }
    setAuthMethodForConsent('google');
    setShowConsentDialog(true);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists() && docSnap.data().userType === 'android') {
        await signOut(auth);
        AuthEvents.androidAccountBlocked();
        setError('This account was created on the Android app. Please use the Resido Android app to sign in.');
      } else if (docSnap.exists() && docSnap.data().isPhoneVerified) {
        AuthEvents.loginSuccess('google');
        clearRateLimit();
        setLoginSuccess(true);
        await new Promise(resolve => setTimeout(resolve, 800));
        navigate('/dashboard');
      } else if (docSnap.exists() && !docSnap.data().isPhoneVerified) {
        AuthEvents.loginSuccess('google');
        clearRateLimit();
        navigate('/institute-selection');
      } else if (allowNewGoogleSignup) {
        AuthEvents.loginSuccess('google');
        navigate('/institute-selection');
      } else {
        await signOut(auth);
        AuthEvents.signupBlocked('new_google_signup_disabled');
        setError('New user sign-ups are currently disabled. Only existing users can sign in.');
      }
    } catch (error) {
      AuthEvents.loginFailure('google', error.code);
      const safeMessage = getSafeErrorMessage(error);
      if (safeMessage) setError(safeMessage);
    } finally {
      setLoading(false);
    }
  };

  // [REMOVED] Apple Sign-In — not supported on Android, removed for platform consistency

  const handlePasswordReset = () => {
    if (!resetEmail) {
      setResetMessage({ text: 'Please enter your email address.', type: 'error' });
      return;
    }
    sendPasswordResetEmail(auth, resetEmail)
      .then(() => {
        AuthEvents.passwordResetRequested();
        setResetMessage({ text: 'Password reset email sent! Please check your inbox.', type: 'success' });
      })
      .catch(() => {
        // [SECURITY] Don't reveal whether the email exists
        setResetMessage({ text: 'If an account exists with this email, a reset link will be sent.', type: 'success' });
      });
  };

  const openResetModal = () => {
    setResetMessage({ text: '', type: '' });
    setResetEmail('');
    setIsModalOpen(true);
  };

  const passwordStrength = getPasswordStrength(password);
  const isAccountLocked = lockoutTime > 0;
  const lockoutMinutes = Math.ceil(lockoutTime / 60000);
  const lockoutSeconds = Math.ceil((lockoutTime % 60000) / 1000);

  return (
    <>
      <div
        className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
        style={{ backgroundColor: THEME.colors.midnightBlue, fontFamily: THEME.fonts.body }}
      >
        {/* --- CSS (reduced — keyframes now in index.css) --- */}
        <style>{`
          @keyframes logoPulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.06); }
          }
          /* animate-blob removed — caused severe jitter on mobile GPUs */
          .animate-logo-pulse { animation: logoPulse 3s ease-in-out infinite; }
          .dark-input {
            background-color: ${THEME.colors.inputBg || '#0F172A'};
            border: 1px solid ${THEME.colors.borderColor};
            color: white;
            transition: all 0.3s ease;
          }
          .dark-input:focus {
            border-color: ${THEME.colors.goldAccent};
            box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15);
            outline: none;
          }
          .dark-input::placeholder { color: #475569; }
        `}</style>

        {/* --- LIVING BACKGROUND --- */}
        <div className="absolute top-0 -left-4 w-72 h-72 bg-amber-500 rounded-full mix-blend-multiply filter blur-[96px] opacity-20"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-[96px] opacity-20"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-blue-600 rounded-full mix-blend-multiply filter blur-[96px] opacity-20"></div>

        {/* --- MAIN CONTENT --- */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 w-full max-w-md flex flex-col items-center"
        >
          {/* --- SUCCESS OVERLAY --- */}
          <AnimatePresence>
            {loginSuccess && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="fixed inset-0 z-50 flex items-center justify-center"
                style={{ backgroundColor: THEME.colors.midnightBlue }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  className="text-center"
                >
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-teal-500/20 flex items-center justify-center">
                    <svg className="w-10 h-10 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <motion.path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                      />
                    </svg>
                  </div>
                  <p className="text-white text-lg font-semibold" style={{ fontFamily: THEME.fonts.heading }}>Welcome back!</p>
                  <p className="text-slate-400 text-sm mt-1">Redirecting to your dashboard...</p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* --- ANIMATED LOGO --- */}
          <div className="mb-2">
            <a href="https://residotech.com" className="block">
              <div className="relative w-24 h-24 mx-auto animate-logo-pulse">
                <div className="absolute -inset-1 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 shadow-lg shadow-white/5"></div>
                <img src="/assets/ic_placeholder_avatar.png" alt="Resido Logo" className="relative z-10 w-full h-full rounded-full object-cover p-[3px]" />
              </div>
            </a>
          </div>

          {/* --- APP NAME + TAGLINE --- */}
          <div className="text-center mb-8">
            <h1 style={{ fontFamily: THEME.fonts.heading }} className="text-3xl font-bold text-white tracking-tight">Resido</h1>
            <p className="text-amber-500/90 text-sm font-medium mt-1">Track. Pay. Relax.</p>
          </div>

          {/* --- LOCKOUT BANNER --- */}
          <AnimatePresence>
            {isAccountLocked && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="w-full mb-4 bg-red-900/30 border border-red-500/50 text-red-200 px-4 py-3 rounded-xl text-sm"
              >
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  Account Temporarily Locked
                </div>
                <p>Too many failed login attempts. Please try again in <strong>{lockoutMinutes}:{String(lockoutSeconds % 60).padStart(2, '0')}</strong>.</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* --- LOGIN CARD --- */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="w-full rounded-3xl shadow-2xl border border-slate-700/50 overflow-hidden"
            style={{ backgroundColor: THEME.colors.midnightSurface }}
          >
            <div className="p-7 md:p-8">

              {/* [SECURITY] Honeypot field — invisible to users, bots will fill it */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }} aria-hidden="true">
                <label htmlFor="website-url">Leave this empty</label>
                <input
                  type="text"
                  id="website-url"
                  name="website_url"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={e => setHoneypot(e.target.value)}
                />
              </div>

              {/* Error Banner */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scaleX: 0.95 }}
                    animate={{ opacity: 1, y: 0, scaleX: 1 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className="bg-red-900/20 border border-red-500/40 text-red-200 px-4 py-3 rounded-xl mb-6 text-sm flex items-start gap-2"
                    role="alert"
                  >
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    <span>{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* --- GOOGLE SIGN-IN (Primary) --- */}
              <button
                onClick={requestGoogleSignIn}
                disabled={loading || !isTermsAccepted || isAccountLocked}
                className="w-full font-semibold py-3.5 px-4 rounded-xl border transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed hover:border-slate-500 active:scale-[0.98]"
                style={{ backgroundColor: THEME.colors.inputBg || '#0F172A', borderColor: THEME.colors.borderColor, color: THEME.colors.textPrimary }}
              >
                {loading && !showEmailLogin ? (
                  <svg className="animate-spin h-5 w-5 text-amber-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <>
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                    <span>Sign in with Google</span>
                  </>
                )}
              </button>

              {/* [REMOVED] Apple Sign-In — removed for Android parity */}

              {/* Signup disabled notice */}
              {configLoaded && !allowNewGoogleSignup && (
                <div className="mt-3 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15 text-center">
                  <p className="text-xs text-amber-400/70">
                    <svg className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    New sign-ups are currently disabled. Only existing users can sign in.
                  </p>
                </div>
              )}

              {/* --- DEMO EMAIL/PASSWORD LOGIN (only visible when demo_instance_enabled is true) --- */}
              {/* Matches Android LoginScreen.kt: AnimatedVisibility(visible = demoInstanceEnabled) */}
              {demoInstanceEnabled && (
                <>
                  {/* --- OR DIVIDER --- */}
                  <div className="my-6 flex items-center">
                    <div className="flex-grow border-t border-slate-700/70"></div>
                    <span className="mx-4 text-xs text-slate-500 uppercase tracking-widest font-semibold">Or</span>
                    <div className="flex-grow border-t border-slate-700/70"></div>
                  </div>

                  {/* --- EMAIL LOGIN TOGGLE / FORM --- */}
                  <AnimatePresence mode="wait">
                    {!showEmailLogin ? (
                      <motion.button
                        key="email-toggle"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        onClick={() => { setShowEmailLogin(true); setError(''); }}
                        className="w-full py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 hover:bg-slate-800/50"
                        style={{ color: THEME.colors.textSecondary, opacity: 0.7 }}
                      >
                        Or Login with Demo Account
                      </motion.button>
                    ) : (
                      <motion.div
                        key="email-form"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <form onSubmit={requestEmailLogin} className="space-y-4">
                          {/* Email */}
                          <div>
                            <label htmlFor="email" className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1.5 block">Email</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                            <svg className="h-4 w-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" /><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" /></svg>
                          </div>
                          <input type="email" id="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="dark-input w-full pl-11 pr-4 py-3 rounded-xl text-sm" placeholder="student@email.com" autoComplete="email" />
                        </div>
                      </div>

                      {/* Password */}
                      <div>
                        <label htmlFor="password" className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1.5 block">Password</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                            <svg className="h-4 w-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                          </div>
                          <input
                            type={passwordVisible ? 'text' : 'password'}
                            id="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="dark-input w-full pl-11 pr-12 py-3 rounded-xl text-sm"
                            placeholder="••••••••"
                            autoComplete="current-password"
                          />
                          <button type="button" onClick={() => setPasswordVisible(!passwordVisible)} className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-slate-300 transition-colors" tabIndex={-1}>
                            {passwordVisible ? (
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            )}
                          </button>
                        </div>

                        {/* [FEATURE] Password strength indicator */}
                        {password && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-2"
                          >
                            <div className="flex gap-1 mb-1">
                              {[1, 2, 3, 4, 5].map(i => (
                                <div
                                  key={i}
                                  className="h-1 flex-1 rounded-full transition-colors duration-300"
                                  style={{ backgroundColor: i <= passwordStrength.score ? passwordStrength.color : '#334155' }}
                                />
                              ))}
                            </div>
                            <p className="text-[10px] font-medium" style={{ color: passwordStrength.color }}>
                              {passwordStrength.label}
                            </p>
                          </motion.div>
                        )}
                      </div>

                      {/* Forgot Password + Remember Me */}
                      <div className="flex items-center justify-between -mt-1">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 text-amber-500 accent-amber-500"
                          />
                          <span className="text-xs text-slate-500">Remember me</span>
                        </label>
                        <button type="button" onClick={openResetModal} className="text-xs font-medium text-amber-500 hover:text-amber-400 transition-colors">
                          Forgot Password?
                        </button>
                      </div>

                      {/* Sign In Button */}
                      <button
                        type="submit"
                        disabled={loading || !isTermsAccepted || isAccountLocked}
                        className="w-full text-slate-900 font-bold text-base py-3.5 px-8 rounded-xl shadow-lg transition-all transform active:scale-[0.97] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100"
                        style={{
                          background: `linear-gradient(135deg, ${THEME.colors.goldAccent}, #D97706)`,
                          boxShadow: '0 4px 14px 0 rgba(245,158,11,0.25)'
                        }}
                      >
                        {loading ? (
                          <svg className="animate-spin h-5 w-5 text-slate-900" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          'Sign In'
                        )}
                      </button>
                    </form>

                    {/* Back to Google */}
                    <button
                      onClick={() => { setShowEmailLogin(false); setError(''); }}
                      className="w-full mt-3 py-2 text-xs text-slate-500 hover:text-slate-300 transition-colors text-center"
                    >
                      ← Back to Sign-In Options
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}

              {/* --- TERMS & CONDITIONS --- */}
              <div className="mt-6 pt-5 border-t border-slate-700/50">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={isTermsAccepted}
                    onChange={(e) => { setIsTermsAccepted(e.target.checked); setError(''); }}
                    className="mt-0.5 w-4 h-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500 focus:ring-offset-0 cursor-pointer accent-amber-500 flex-shrink-0"
                  />
                  <label htmlFor="terms-checkbox" className="text-xs text-slate-400 leading-relaxed cursor-pointer select-none">
                    I agree to the{' '}
                    <a href="https://residotech.com/terms-of-service.html" className="text-amber-500 hover:text-amber-400 underline underline-offset-2" target="_blank" rel="noopener noreferrer">Terms of Service</a>,{' '}
                    <a href="https://residotech.com/privacy-policy.html" className="text-amber-500 hover:text-amber-400 underline underline-offset-2" target="_blank" rel="noopener noreferrer">Privacy Policy</a> &{' '}
                    <a href="https://residotech.com/eula.html" className="text-amber-500 hover:text-amber-400 underline underline-offset-2" target="_blank" rel="noopener noreferrer">End-User License Agreement</a>
                  </label>
                </div>
              </div>
            </div>
          </motion.div>

          {/* --- BOTTOM FOOTER --- */}
          <div className="mt-8 text-center opacity-60">
            <a href="mailto:support@residotech.com?subject=Login%20Support%20Request" className="text-xs text-amber-500 hover:text-amber-400 transition-colors underline underline-offset-2">
              Contact Support
            </a>
            <p className="text-[10px] text-slate-600 mt-2">Version web-1.1</p>
          </div>
        </motion.div>
      </div>

      {/* --- RESET PASSWORD MODAL --- */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="rounded-2xl shadow-2xl p-8 w-full max-w-md border border-slate-700"
              style={{ backgroundColor: THEME.colors.midnightSurface }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-amber-500/10 rounded-full text-amber-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <h3 style={{ fontFamily: THEME.fonts.heading }} className="text-xl font-bold text-white">Reset Password</h3>
              </div>

              <p className="text-slate-400 mb-6 text-sm">Enter your registered email address and we'll send you a link to reset your password.</p>

              {resetMessage.text && (
                <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${resetMessage.type === 'success' ? 'bg-teal-900/30 text-teal-300 border border-teal-500/30' : 'bg-red-900/30 text-red-300 border border-red-500/30'}`}>
                  {resetMessage.text}
                </div>
              )}

              <div>
                <label htmlFor="reset-email" className="sr-only">Email Address</label>
                <input type="email" id="reset-email" placeholder="Enter your email" className="dark-input w-full px-4 py-3 rounded-xl text-sm" value={resetEmail} onChange={e => setResetEmail(e.target.value)} />
              </div>

              <div className="mt-8 flex justify-end space-x-3">
                <button onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm font-medium">Cancel</button>
                <button onClick={handlePasswordReset} className="px-6 py-2.5 rounded-lg font-bold text-slate-900 text-sm shadow-md transition-transform active:scale-95" style={{ background: THEME.colors.goldAccent }}>Send Reset Email</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- CONSENT DIALOG (matches Android LoginScreen.kt showConsentDialog) --- */}
      <AnimatePresence>
        {showConsentDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="rounded-2xl shadow-2xl p-8 w-full max-w-md border border-slate-700 flex flex-col items-center"
              style={{ backgroundColor: THEME.colors.midnightSurface }}
            >
              {/* App Logo */}
              <img
                src="/assets/ic_placeholder_avatar.png"
                alt="Resido Logo"
                className="w-16 h-16 rounded-full mb-4"
              />

              <h3 style={{ fontFamily: THEME.fonts.heading }} className="text-lg font-bold text-white mb-4">
                Resido requests access to:
              </h3>

              {/* Data access items */}
              <div className="w-full rounded-xl p-4 mb-4 space-y-3" style={{ backgroundColor: 'rgba(30,41,59,0.5)' }}>
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm text-slate-200">Your email address</span>
                </div>
                {authMethodForConsent === 'google' && (
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-sm text-slate-200">Your name and profile photo</span>
                  </div>
                )}
              </div>

              <p className="text-xs text-slate-400 text-center mb-6 leading-relaxed">
                Your data is used to create your account and personalize your experience. Your information may be shared with your institution for administrative purposes.
              </p>

              {/* Continue button */}
              <button
                onClick={() => {
                  setShowConsentDialog(false);
                  if (authMethodForConsent === 'google') {
                    handleGoogleSignIn();
                  } else if (authMethodForConsent === 'email') {
                    handleLogin();
                  }
                }}
                className="w-full font-bold text-base py-3.5 rounded-xl shadow-lg transition-all transform active:scale-[0.97] flex items-center justify-center gap-2"
                style={{ background: `linear-gradient(135deg, ${THEME.colors.goldAccent}, #D97706)`, color: '#0F172A', boxShadow: '0 4px 14px 0 rgba(245,158,11,0.25)' }}
              >
                {authMethodForConsent === 'google' && (
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
                )}
                {authMethodForConsent === 'google' ? 'Continue with Google' : 'Sign In'}
              </button>

              <button
                onClick={() => setShowConsentDialog(false)}
                className="mt-3 px-4 py-2 text-sm text-slate-500 hover:text-slate-300 transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Login;